/**
 * Отметка «этот диалог открыт на устройстве» (CLIENT_WEB.md §8.5 п. 1–2).
 *
 * Одно значение обслуживает сразу двух потребителей:
 *  - кадр `active_chat` по WebSocket — серверное подавление push (BS §9.3);
 *  - запись в `app-v1-device` — локальное подавление в service worker'е,
 *    единственно возможное в режиме `polling_fallback` (GAP-COMMON-01).
 *
 * Оба обновляются в одни и те же моменты, поэтому живут в одном месте:
 * открытие чата, уход с чата, `visibilitychange`, `blur`/`focus` окна и
 * обновление раз в 45 с (TTL записи — 60 с).
 */
import { markActiveConversation } from '@shared/db'
import { dismissConversationNotifications } from '@shared/push'
import { syncEngine } from '@shared/sync/instance'

/** TTL записи 60 с (§2.4) — обновляем с запасом, как и кадр `active_chat`. */
const REFRESH_MS = 45_000

let openConversationId: string | null = null
let refreshTimer: ReturnType<typeof setInterval> | null = null

/** Экран чата сообщает, какой диалог открыт; `null` — ни одного. */
export function setActiveConversation(conversationId: string | null): void {
  if (openConversationId === conversationId) return
  openConversationId = conversationId

  publish()

  /*
   * §8.5 п. 4: открытие диалога снимает показанные уведомления его серии.
   * `collapse_key` берётся из реплики, а не собирается по шаблону.
   */
  if (conversationId) void dismissConversationNotifications(conversationId).catch(() => undefined)
}

/** Значение, которое должно быть видно сейчас: при скрытой вкладке — всегда null. */
function effectiveId(): string | null {
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return null
  if (typeof document !== 'undefined' && !document.hasFocus()) return null
  return openConversationId
}

function publish(): void {
  const id = effectiveId()
  syncEngine()?.setActiveConversation(id)
  void markActiveConversation(id).catch(() => undefined)
}

export function startActiveChatMirror(): () => void {
  const onVisibility = (): void => {
    // §8.5 п. 2: свёрнутое окно с открытым чатом подавление НЕ включает.
    publish()
  }

  document.addEventListener('visibilitychange', onVisibility)
  globalThis.addEventListener('focus', onVisibility)
  globalThis.addEventListener('blur', onVisibility)

  refreshTimer = setInterval(() => {
    // Обновляем метку времени, даже если диалог не менялся: иначе запись
    // протухнет через 60 с и подавление перестанет работать.
    if (effectiveId()) publish()
  }, REFRESH_MS)

  return () => {
    document.removeEventListener('visibilitychange', onVisibility)
    globalThis.removeEventListener('focus', onVisibility)
    globalThis.removeEventListener('blur', onVisibility)
    if (refreshTimer) clearInterval(refreshTimer)
    refreshTimer = null
  }
}
